import { useState, useEffect } from 'react';
import { adminApi } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Settings, Loader2, Save, Plus, Trash2, Building2, MessageCircle, ExternalLink } from 'lucide-react';

const extractSmartsuppKey = (raw) => {
  const match = (raw || '').match(/[a-fA-F0-9]{40}/);
  return match ? match[0].toLowerCase() : raw;
};

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    transfer_fee_percent: 2,
    min_transfer_amount: 100,
    max_transfer_amount: 1000000,
    bank_name: '',
    bank_account_number: '',
    bank_account_name: '',
    bank_accounts: [],
    admin_email: '',
    whatsapp_link: '',
    smartsupp_key: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      const data = response.data;
      
      // If no bank_accounts array but legacy fields exist, migrate them
      let bankAccounts = data.bank_accounts || [];
      if (bankAccounts.length === 0 && data.bank_name) {
        bankAccounts = [{
          bank_name: data.bank_name,
          account_number: data.bank_account_number || '',
          account_name: data.bank_account_name || ''
        }];
      }
      
      setSettings({ ...settings, ...data, bank_accounts: bankAccounts });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Update legacy fields with first bank account for backward compatibility
      const updatedSettings = { ...settings };
      if (settings.bank_accounts.length > 0) {
        updatedSettings.bank_name = settings.bank_accounts[0].bank_name;
        updatedSettings.bank_account_number = settings.bank_accounts[0].account_number;
        updatedSettings.bank_account_name = settings.bank_accounts[0].account_name;
      }
      
      await adminApi.updateSettings(updatedSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addBankAccount = () => {
    setSettings({
      ...settings,
      bank_accounts: [...settings.bank_accounts, { bank_name: '', account_number: '', account_name: '' }]
    });
  };

  const removeBankAccount = (index) => {
    const newAccounts = settings.bank_accounts.filter((_, i) => i !== index);
    setSettings({ ...settings, bank_accounts: newAccounts });
  };

  const updateBankAccount = (index, field, value) => {
    const newAccounts = [...settings.bank_accounts];
    newAccounts[index] = { ...newAccounts[index], [field]: value };
    setSettings({ ...settings, bank_accounts: newAccounts });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-white font-['Outfit']">Settings</h1>
      <form onSubmit={handleSave} className="space-y-6">
        
        {/* Bank Accounts - Multiple */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FFD700]" />
              Bank Accounts for Deposits
            </CardTitle>
            <Button 
              type="button" 
              onClick={addBankAccount} 
              variant="outline" 
              size="sm" 
              className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Bank
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.bank_accounts.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">No bank accounts added. Click "Add Bank" to add one.</p>
            ) : (
              settings.bank_accounts.map((account, index) => (
                <div key={index} className="p-4 bg-[#121212] rounded-lg border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#FFD700]">Bank Account #{index + 1}</span>
                    <Button 
                      type="button" 
                      onClick={() => removeBankAccount(index)} 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid gap-3">
                    <div>
                      <Label className="text-zinc-300 text-xs">Bank Name</Label>
                      <Input 
                        value={account.bank_name || ''} 
                        onChange={(e) => updateBankAccount(index, 'bank_name', e.target.value)} 
                        className="bg-[#0A0A0A] border-white/10 text-white" 
                        placeholder="e.g., GTBank, First Bank" 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-300 text-xs">Account Number</Label>
                        <Input 
                          value={account.account_number || ''} 
                          onChange={(e) => updateBankAccount(index, 'account_number', e.target.value)} 
                          className="bg-[#0A0A0A] border-white/10 text-white" 
                          placeholder="0123456789" 
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-xs">Account Name</Label>
                        <Input 
                          value={account.account_name || ''} 
                          onChange={(e) => updateBankAccount(index, 'account_name', e.target.value)} 
                          className="bg-[#0A0A0A] border-white/10 text-white" 
                          placeholder="John Doe" 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Live Chat Integration */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#FFD700]" />
              Live Chat (Smartsupp)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Smartsupp Chat Key</Label>
                {settings.smartsupp_key ? (
                  <span data-testid="smartsupp-status" className="text-xs font-medium text-green-500 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> Connected
                  </span>
                ) : (
                  <span data-testid="smartsupp-status" className="text-xs font-medium text-amber-500">Not configured</span>
                )}
              </div>
              <Input 
                data-testid="smartsupp-key-input"
                value={settings.smartsupp_key || ''} 
                onChange={(e) => setSettings({...settings, smartsupp_key: extractSmartsuppKey(e.target.value)})} 
                className="bg-[#121212] border-white/10 text-white font-mono" 
                placeholder="Paste your Smartsupp key or the full chat code snippet"
              />
              <p className="text-xs text-zinc-500 mt-1">
                You can paste the entire <code>&lt;script&gt;</code> snippet from{' '}
                <a 
                  href="https://www.smartsupp.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-[#FFD700] hover:underline inline-flex items-center gap-1"
                >
                  smartsupp.com <ExternalLink className="w-3 h-3" />
                </a>
                {' '}→ Settings → Chat box → Chat code — the key is extracted automatically.
              </p>
            </div>
            <div className="p-3 bg-[#121212] rounded-lg border border-white/10">
              <p className="text-xs text-zinc-400">
                <strong className="text-[#FFD700]">How it works:</strong> A gold chat button appears for visitors and customers on your site. 
                You reply from the{' '}
                <a href="https://app.smartsupp.com" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:underline">Smartsupp dashboard</a>
                {' '}or mobile app — no need to be on your website!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Transfer Settings */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader><CardTitle className="text-white">Transfer Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300">Transfer Fee (%)</Label>
              <Input 
                type="number" 
                step="0.1" 
                value={settings.transfer_fee_percent} 
                onChange={(e) => setSettings({...settings, transfer_fee_percent: parseFloat(e.target.value) || 0})} 
                className="bg-[#121212] border-white/10 text-white" 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Min Transfer (₦)</Label>
                <Input 
                  type="number" 
                  value={settings.min_transfer_amount} 
                  onChange={(e) => setSettings({...settings, min_transfer_amount: parseFloat(e.target.value) || 0})} 
                  className="bg-[#121212] border-white/10 text-white" 
                />
              </div>
              <div>
                <Label className="text-zinc-300">Max Transfer (₦)</Label>
                <Input 
                  type="number" 
                  value={settings.max_transfer_amount} 
                  onChange={(e) => setSettings({...settings, max_transfer_amount: parseFloat(e.target.value) || 0})} 
                  className="bg-[#121212] border-white/10 text-white" 
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Settings */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader><CardTitle className="text-white">Contact</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300">Admin Email</Label>
              <Input 
                type="email" 
                value={settings.admin_email || ''} 
                onChange={(e) => setSettings({...settings, admin_email: e.target.value})} 
                className="bg-[#121212] border-white/10 text-white" 
              />
            </div>
            <div>
              <Label className="text-zinc-300">WhatsApp Link</Label>
              <Input 
                value={settings.whatsapp_link || ''} 
                onChange={(e) => setSettings({...settings, whatsapp_link: e.target.value})} 
                className="bg-[#121212] border-white/10 text-white" 
                placeholder="https://wa.me/..." 
              />
            </div>
          </CardContent>
        </Card>

        <Button data-testid="settings-save-btn" type="submit" disabled={saving} className="w-full bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Settings</>}
        </Button>
      </form>
    </div>
  );
}
